import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, MapPin, Clock, Trophy, ArrowRight, FileText, Download, ExternalLink, DollarSign, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Conference() {
  const [c, setC] = useState({});
  const [img, setImg] = useState({});
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => setC(r.data)).catch(() => {});
    axios.get(`${API}/site-images`).then(r => setImg(r.data)).catch(() => {});
    axios.get(`${API}/case-comp-documents`).then(r => setDocuments(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="conference-page">
      {/* Hero Header */}
      <section className="relative bg-[#0F172A] text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-400 rounded-full blur-[150px]" />
        </div>
        <div className="container-main relative z-10 text-center">
          <Badge variant="outline" className="border-white/20 text-white/70 mb-4 text-xs font-medium tracking-wider uppercase">
            {c.confBadge || "Competition"}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {c.confName || "Case Competition"}
          </h1>
          <p className="mt-4 text-slate-300 text-base max-w-2xl mx-auto leading-relaxed">
            {c.confTagline || "Compete, collaborate, and tackle real healthcare business challenges."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-slate-300">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{c.confDate || "Date TBA"}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{c.confLocation || "Location TBA"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{c.confTime || "Time TBA"}</span>
            </div>
          </div>
          {c.confRegLink && (
            <a href={c.confRegLink} target="_blank" rel="noopener noreferrer" data-testid="conf-register-btn">
              <Button className="mt-8 bg-white text-[#0F172A] hover:bg-slate-100 rounded-full px-8 py-3 text-sm font-semibold shadow-lg">
                Register Now <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          )}
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 lg:py-20 bg-white" data-testid="conf-overview">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">About the Competition</p>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {c.confOverviewTitle || "What to Expect"}
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed text-[15px]">
                {c.confOverviewText || "This case competition brings together students to solve real-world healthcare business challenges. Teams will analyze a case study and present their solutions to a panel of industry judges."}
              </p>
              <p className="text-slate-600 mt-3 leading-relaxed text-[15px]">
                {c.confOverviewText2 || "Stay tuned for case details, judging criteria, and prize information."}
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100">
              {img.confHero ? (
                <img src={img.confHero} alt="Case Competition" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                  <Trophy className="h-16 w-16 mb-3" />
                  <p className="text-sm">Competition image — upload via admin</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Separator className="bg-slate-100" />

      {/* Competition Details */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="conf-details">
        <div className="container-main max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Details</p>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {c.confDetailsTitle || "Competition Details"}
          </h2>

          <div className="space-y-4">
            {[
              { icon: DollarSign, label: "Entry Fee", value: c.confDetailsFee || "$30 per team" },
              { icon: Users, label: "Team Size", value: c.confDetailsTeamSize || "Max 4 people per team" },
              { icon: Calendar, label: "Round 1 Due", value: c.confDetailsRound1 || "September 26th" },
              { icon: Calendar, label: "Final Placement", value: c.confDetailsPlacement || "October 3rd" },
              { icon: MapPin, label: "Round 2", value: c.confDetailsRound2 || "November 14th at McCombs" },
              { icon: Trophy, label: "Prizes", value: c.confDetailsPrizes || "Cash prizes for the winner" },
              { icon: FileText, label: "Registration", value: c.confDetailsRegistration || "Google Form with Venmo — case emailed on rolling submissions" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100">
                <div className="w-10 h-10 rounded-lg bg-[#0F172A] flex items-center justify-center shrink-0">
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{item.label}</p>
                  <p className="text-[15px] text-[#0F172A] font-medium mt-0.5">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Separator className="bg-slate-100" />

      {/* Documents Section */}
      <section className="py-16 lg:py-20 bg-white" data-testid="conf-documents">
        <div className="container-main">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Resources</p>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {c.confDocumentsTitle || "Competition Documents"}
          </h2>
          <p className="text-slate-500 text-sm mb-8 max-w-2xl">
            {c.confDocumentsSubtitle || "Download important materials for the case competition below."}
          </p>

          {documents.length > 0 ? (
            <div className="grid gap-4 max-w-2xl">
              {documents.map((doc, i) => (
                <Card key={doc.id || i} data-testid={`case-comp-doc-${i}`} className="border border-slate-100 shadow-sm hover:shadow-md transition-all">
                  <CardContent className="p-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                        <FileText className="h-5 w-5 text-[#0F172A]" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-sm text-[#0F172A] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{doc.title}</h4>
                        {doc.description && <p className="text-slate-500 text-xs mt-0.5 truncate">{doc.description}</p>}
                      </div>
                    </div>
                    {doc.file_url && (
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="rounded-full text-xs shrink-0">
                          <Download className="h-3.5 w-3.5 mr-1" /> Download
                        </Button>
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No documents uploaded yet.</p>
              <p className="text-slate-400 text-xs mt-1">Documents will appear here once added via the admin panel.</p>
            </div>
          )}
        </div>
      </section>

      {/* Registration CTA */}
      <section className="py-16 bg-[#0F172A] text-white text-center" data-testid="conf-cta">
        <div className="container-main">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {c.confCTATitle || "Ready to Compete?"}
          </h2>
          <p className="mt-3 text-slate-300 max-w-md mx-auto text-sm">
            {c.confCTAText || "Registration is open. Gather your team and sign up for the case competition today."}
          </p>
          {c.confRegLink && (
            <a href={c.confRegLink} target="_blank" rel="noopener noreferrer" data-testid="conf-cta-register">
              <Button className="mt-6 bg-white text-[#0F172A] hover:bg-slate-100 rounded-full px-8 py-3 text-sm font-semibold">
                Register Now <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          )}
        </div>
      </section>
    </div>
  );
}
